import { customerHelpers } from '@helpers/customer.helpers'
import { TicketOption, TicketCategory } from './ticket.types'
import type { TTinyCustomer } from '@/core/types'

// ============================================================================
// Constants
// ============================================================================

/**
 * Allowed customer short statuses for purchasing
 * A = Active Associate
 * B = Pending Associate
 * M = Member (custom status for GLIC)
 */
const ALLOWED_SHORT_STATUSES = ['A', 'B', 'M']

// ============================================================================
// Price & Status Formatting
// ============================================================================

/**
 * Format price with currency symbol
 */
export const formatPrice = (price: number, currency: string, spaceBetween: boolean = false): string => {
    const symbols: Record<string, string> = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        THB: '฿',
        JPY: '¥',
        KRW: '₩',
        VND: '₫'
    }
    const symbol = symbols[currency] || currency
    const formattedPrice = price.toLocaleString('en-US')
    return spaceBetween ? `${symbol} ${formattedPrice}` : `${symbol}${formattedPrice}`
}

/**
 * Get status text for display
 */
export const getStatusText = (status: 'available' | 'available_soon' | 'sold_out'): string => {
    const texts: Record<string, string> = {
        available: 'available',
        available_soon: 'available_soon',
        sold_out: 'sold_out'
    }
    return texts[status] || status
}

// ============================================================================
// Customer Eligibility
// ============================================================================

/**
 * Get customer short status from type and status
 * Uses customerHelpers.getStatus() from @repo/unicity
 * Note: TTinyCustomer has compatible status/type properties with TCustomer
 */
export const getCustomerShortStatus = (customer: TTinyCustomer | null): string | null => {
    if (!customer) return null
    // TTinyCustomer has status and type properties compatible with TCustomer
    return customerHelpers.getStatus(customer as unknown as Parameters<typeof customerHelpers.getStatus>[0])
}

/**
 * Check if customer is eligible to purchase
 * Only customers with short status A, B, or M can purchase
 */
export const isCustomerEligibleToPurchase = (customer: TTinyCustomer | null): boolean => {
    if (!customer) return false

    // TTinyCustomer has status and type properties compatible with TCustomer
    const shortStatus = customerHelpers.getStatus(customer as unknown as Parameters<typeof customerHelpers.getStatus>[0])
    return ALLOWED_SHORT_STATUSES.includes(shortStatus)
}

// ============================================================================
// Period Helpers
// ============================================================================

// Hawaii Standard Time, UTC-10, no DST — the "slowest" inhabited timezone.
// Periods are interpreted in this zone so a ticket closes last (lenient).
// ponytail: fixed offset string, no tz lib needed since HST has no DST.
const TICKET_TZ_OFFSET = '-10:00'

/**
 * Parse date string "2026-01-20" as start-of-day in Hawaii time (UTC-10)
 */
export const parseStartDate = (dateStr: string): Date => {
    const date = new Date(`${dateStr.slice(0, 10)}T00:00:00.000${TICKET_TZ_OFFSET}`)
    return isNaN(date.getTime()) ? new Date() : date
}

/**
 * Parse date string "2026-04-30" as end-of-day in Hawaii time (UTC-10)
 */
export const parseEndDate = (dateStr: string): Date => {
    const date = new Date(`${dateStr.slice(0, 10)}T23:59:59.999${TICKET_TZ_OFFSET}`)
    return isNaN(date.getTime()) ? new Date() : date
}

/**
 * Check if two periods intersect or are the same
 * Uses period_start and period_end from TicketOption
 */
export const doPeriodsIntersect = (ticket1: TicketOption, ticket2: TicketOption): boolean => {
    const start1 = parseStartDate(ticket1.period_start)
    const end1 = parseEndDate(ticket1.period_end)
    const start2 = parseStartDate(ticket2.period_start)
    const end2 = parseEndDate(ticket2.period_end)

    // Two periods intersect if one starts before the other ends
    return start1 <= end2 && start2 <= end1
}

/**
 * Group tickets by intersecting periods
 * Returns array of ticket groups where each group contains tickets with overlapping periods
 */
export const groupTicketsByPeriod = (tickets: TicketOption[]): TicketOption[][] => {
    const groups: TicketOption[][] = []
    const processed = new Set<string>()

    for (const ticket of tickets) {
        if (processed.has(ticket.item_code)) continue

        // Find all tickets that intersect with this one
        const group = tickets.filter(t => {
            if (processed.has(t.item_code)) return false
            return t.item_code === ticket.item_code || doPeriodsIntersect(ticket, t)
        })

        group.forEach(t => processed.add(t.item_code))
        groups.push(group)
    }

    return groups
}

// ============================================================================
// Ticket Status
// ============================================================================

/**
 * Get ticket status based on current date and period
 * Uses period_start and period_end from TicketOption
 * - Before period start (00:00) → 'available_soon'
 * - Within period → 'available'
 * - After period end → 'sold_out'
 */
export const getTicketStatus = (ticket: TicketOption): 'available' | 'available_soon' | 'sold_out' => {
    const now = new Date()
    const startDate = parseStartDate(ticket.period_start)
    const endDate = parseEndDate(ticket.period_end)

    if (now < startDate) {
        return 'available_soon'
    } else if (now > endDate) {
        return 'sold_out'
    }
    return 'available'
}

/**
 * Get tickets with computed status based on current date
 */
export const getTicketsWithStatus = (tickets: TicketOption[]): TicketOption[] => {
    return tickets.map(ticket => ({
        ...ticket,
        status: getTicketStatus(ticket)
    }))
}

// ============================================================================
// Early Bird Price Eligibility
// ============================================================================

/**
 * Check if customer is eligible for early bird pricing on a non-early-bird ticket.
 *
 * USHOP-2914: the "joined <=30 days AND hasn't already bought" rule (incl. the
 * 1-ID-1-ticket limit) is now decided by the BE and delivered as
 * customer.isCanBuyEarlyBird. FE just trusts that flag — no client-side joinDate math.
 *
 * Still gated on the ticket itself: must have a real early_bird_price and not be
 * an Early Bird ticket (those already carry early-bird pricing).
 *
 * @param ticket - The ticket to check
 * @param canBuyEarlyBird - BE verdict from customer.isCanBuyEarlyBird
 * @returns true if the customer should get early_bird_price on this ticket
 */
export const isEligibleForEarlyBirdPrice = (ticket: TicketOption, canBuyEarlyBird?: boolean): boolean => {
    // Must have early_bird_price defined and be less than regular price
    if (!ticket.early_bird_price || ticket.early_bird_price >= ticket.price) {
        return false
    }

    // Must not be an Early Bird ticket (those already have early bird pricing)
    if (ticket.ticket_type.toLowerCase().includes('early bird')) {
        return false
    }

    return !!canBuyEarlyBird
}

/**
 * Get the effective price for a ticket based on customer eligibility
 * - If eligible for early bird pricing → return early_bird_price
 * - Otherwise → return regular price
 */
export const getEffectivePrice = (ticket: TicketOption, canBuyEarlyBird?: boolean): number => {
    if (isEligibleForEarlyBirdPrice(ticket, canBuyEarlyBird)) {
        return ticket.early_bird_price || ticket.price
    }
    return ticket.price
}

// ============================================================================
// Default Ticket Selection
// ============================================================================

/**
 * Get default ticket based on period intersection logic
 *
 * Logic:
 * 1. Group tickets by intersecting periods
 * 2. For each group, default to the FIRST item in that group
 * 3. Return the first available ticket from the current time period
 */
export const getDefaultTicket = (tickets: TicketOption[], _joinDate?: string): string => {
    const now = new Date()

    // Find tickets that are currently available or will be available soon
    const currentlyRelevant = tickets.filter(t => {
        const endDate = parseEndDate(t.period_end)
        return now <= endDate // Not yet sold out
    })

    if (currentlyRelevant.length === 0) {
        // All tickets are sold out, return first one
        return tickets[0]?.item_code || ''
    }

    // Group by intersecting periods
    const groups = groupTicketsByPeriod(currentlyRelevant)

    // Find the group that contains currently available tickets
    for (const group of groups) {
        const availableInGroup = group.filter(t => t.status === 'available')
        if (availableInGroup.length > 0) {
            // Return the FIRST item in this group
            return group[0]?.item_code || ''
        }
    }

    // If no available tickets, return first upcoming
    const upcoming = currentlyRelevant.filter(t => t.status === 'available_soon')
    if (upcoming.length > 0) {
        return upcoming[0]?.item_code || ''
    }

    return currentlyRelevant[0]?.item_code || ''
}

// ============================================================================
// Ticket Eligibility
// ============================================================================

/**
 * Check if a ticket can be selected/purchased
 *
 * Simple logic:
 * - Ticket must be 'available' to be selectable
 * - All available tickets can be purchased by any eligible customer
 * - Price may vary based on early bird pricing eligibility
 */
export const isEligibleForTicket = (ticket: TicketOption): boolean => {
    // Only available tickets can be selected
    return ticket.status === 'available'
}

// ============================================================================
// Category-Based Selection
// ============================================================================

/**
 * Get unique categories from tickets for homepage display
 * Returns first ticket of each category for display purposes
 */
export const getCategoryDisplayTickets = (tickets: TicketOption[]): TicketOption[] => {
    const categoryMap = new Map<TicketCategory, TicketOption>()

    for (const ticket of tickets) {
        if (!categoryMap.has(ticket.category)) {
            categoryMap.set(ticket.category, ticket)
        }
    }

    return Array.from(categoryMap.values())
}

/**
 * Get all tickets in a specific category
 */
export const getTicketsByCategory = (tickets: TicketOption[], category: TicketCategory): TicketOption[] => {
    return tickets.filter(t => t.category === category)
}

/**
 * Check if a category is eligible (has at least one available ticket)
 */
export const isCategoryEligible = (tickets: TicketOption[], category: TicketCategory): boolean => {
    const categoryTickets = getTicketsByCategory(tickets, category)
    return categoryTickets.some(t => t.status === 'available')
}

/**
 * Get default category based on available tickets
 */
export const getDefaultCategory = (tickets: TicketOption[]): TicketCategory => {
    // Priority: early_bird > regular > late
    const priorities: TicketCategory[] = ['early_bird', 'regular', 'late']

    for (const category of priorities) {
        if (isCategoryEligible(tickets, category)) {
            return category
        }
    }

    return 'early_bird' // fallback
}
